import {
  Button,
  Group,
  Modal,
  Select,
  Stack,
  TextInput,
} from '@mantine/core';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { useState } from 'react';
import { InsuranceCredential } from '../../users/types/rbac';

interface CredentialFormModalProps {
  credential?: InsuranceCredential | null;
  isOpen: boolean;
  onAddCredential: (credential: Omit<InsuranceCredential, 'id'>) => void;
  onUpdateCredential: (id: string, updates: Partial<InsuranceCredential>) => void;
  onClose: () => void;
}

export function CredentialFormModal({
  credential,
  isOpen,
  onAddCredential,
  onUpdateCredential,
  onClose,
}: CredentialFormModalProps) {
  const [insuranceCompany, setInsuranceCompany] = useState(
    credential?.insuranceCompany || ''
  );
  const [providerId, setProviderId] = useState(credential?.providerId || '');
  const [status, setStatus] = useState<'active' | 'pending' | 'inactive'>(
    credential?.status || 'pending'
  );
  const [effectiveDate, setEffectiveDate] = useState(
    credential?.effectiveDate || ''
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!insuranceCompany || !providerId || !effectiveDate) {
      alert('Please fill in all required fields');
      return;
    }

    if (credential) {
      onUpdateCredential(credential.id, {
        insuranceCompany,
        providerId,
        status,
        effectiveDate,
      });
    } else {
      onAddCredential({
        insuranceCompany,
        providerId,
        status,
        effectiveDate,
      });
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={credential ? 'Edit Credential' : 'Add Insurance Credential'}
      size="md"
      centered
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <TextInput
            label="Insurance Company"
            placeholder="e.g., Blue Cross Blue Shield"
            value={insuranceCompany}
            onChange={(e) => setInsuranceCompany(e.target.value)}
            required
          />

          <TextInput
            label="Provider ID/Number"
            placeholder="e.g., 123456789"
            value={providerId}
            onChange={(e) => setProviderId(e.target.value)}
            required
          />

          <Select
            label="Status"
            data={[
              { value: 'pending', label: 'Pending' },
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
            value={status}
            onChange={(value) =>
              setStatus((value as 'active' | 'pending' | 'inactive') || 'pending')
            }
            required
          />

          <TextInput
            label="Effective Date"
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            required
          />

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button
              type="submit"
              leftSection={<IconDeviceFloppy size={16} />}
            >
              {credential ? 'Update' : 'Add'} Credential
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
